import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Shield, Scale, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LATEST_POLICY_VERSION } from '../../lib/version';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLICY_VERSION = LATEST_POLICY_VERSION;

export default function PolicyAcceptanceModal({ isOpen, onAccepted }) {
  const { updatePolicyAcceptance } = useAuth();
  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canAccept = termsChecked && privacyChecked;

  const handleAccept = async () => {
    if (!canAccept) return;
    
    setIsSubmitting(true);
    setError(null);

    try {
      // Cookie-based auth — no need to attach a Bearer header. The global fetch
      // patch adds credentials: 'include' automatically.
      const response = await fetch(`${API_URL}/api/auth/accept-policy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          policy_version: POLICY_VERSION 
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to accept policy');
      }

      const data = await response.json();
      
      // Update local storage with new accepted timestamp
      const storedUser = localStorage.getItem('auth_user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        userData.policy_accepted_at = data.accepted_at;
        userData.policy_version = data.policy_version;
        localStorage.setItem('auth_user', JSON.stringify(userData));
      }

      // Update auth context immediately
      updatePolicyAcceptance(data.accepted_at, data.policy_version);

      onAccepted();
    } catch (err) {
      console.error('Policy acceptance error:', err);
      setError(err.message || 'Failed to accept policy. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openTerms = () => {
    window.open('/terms', '_blank');
  };

  const openPrivacy = () => {
    window.open('/privacy', '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-lg" 
        data-testid="policy-acceptance-modal"
        hideCloseButton={true}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-teal-700" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Policy Update Required
              </DialogTitle>
              <DialogDescription className="text-sm text-slate-500">
                Please review and accept our updated policies to continue
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Info Banner */}
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Important Policy Updates</p>
                <p className="text-amber-700">
                  We've updated our Terms of Use and Privacy Policy to improve transparency about data handling, AI usage, and your rights. Please review these documents before continuing.
                </p>
              </div>
            </div>
          </div>

          {/* Policy Links */}
          <div className="space-y-3">
            {/* Terms of Use */}
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Checkbox
                id="terms-checkbox"
                checked={termsChecked}
                onCheckedChange={setTermsChecked}
                data-testid="terms-checkbox"
                className="mt-0.5"
              />
              <div className="flex-1">
                <label 
                  htmlFor="terms-checkbox" 
                  className="text-sm font-medium text-slate-900 cursor-pointer"
                >
                  I have read and agree to the Terms of Use
                </label>
                <p className="text-xs text-slate-500 mt-1">
                  Includes entity definitions, scoring disclaimers, and user responsibilities
                </p>
                <button
                  onClick={openTerms}
                  className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-800 font-medium mt-2"
                  data-testid="view-terms-link"
                >
                  <Scale className="w-3 h-3" />
                  View Terms of Use
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Privacy Policy */}
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <Checkbox
                id="privacy-checkbox"
                checked={privacyChecked}
                onCheckedChange={setPrivacyChecked}
                data-testid="privacy-checkbox"
                className="mt-0.5"
              />
              <div className="flex-1">
                <label 
                  htmlFor="privacy-checkbox" 
                  className="text-sm font-medium text-slate-900 cursor-pointer"
                >
                  I have read and agree to the Privacy Policy
                </label>
                <p className="text-xs text-slate-500 mt-1">
                  Covers multi-tenancy, data retention, AI use, and your privacy rights
                </p>
                <button
                  onClick={openPrivacy}
                  className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-800 font-medium mt-2"
                  data-testid="view-privacy-link"
                >
                  <Shield className="w-3 h-3" />
                  View Privacy Policy
                  <ExternalLink className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Accept Button */}
          <Button
            onClick={handleAccept}
            disabled={!canAccept || isSubmitting}
            className="w-full bg-teal-700 hover:bg-teal-800 text-white py-6"
            data-testid="accept-policy-btn"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Accept & Continue'
            )}
          </Button>

          {/* Version Info */}
          <p className="text-xs text-center text-slate-400">
            Policy Version {POLICY_VERSION} • Effective March 1, 2026
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
